/**
 * Facebook Pixel tracking utility for TempStars
 * Optimized implementation with Advanced Matching, UTM tracking, and deduplication
 * 
 * Pixel ID: 1707077042884387
 * The pixel base code is installed in the main Framework7 app,
 * making window.fbq available globally.
 * 
 * Features:
 * - Advanced Matching (hashed user data)
 * - Event deduplication (eventID for CAPI integration)
 * - UTM parameter tracking
 * - Geographic data
 * - Environment detection (dev/staging/production)
 */

// Extend Window interface to include fbq
declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom',
      eventName: string,
      parameters?: Record<string, any>,
      options?: Record<string, any>
    ) => void;
  }
}

/**
 * User types for sign-up completion events
 */
export enum UserType {
  DENTAL_PROFESSIONAL = 'Dental Professional',
  DENTAL_OFFICE = 'Dental Office'
}

/**
 * Environment detection
 * TempStars environments:
 * - Production: app.tempstars.com
 * - Staging: app.beta.tempstars.com
 * - Development: localhost
 */
const getEnvironment = (): 'production' | 'staging' | 'development' => {
  if (typeof window === 'undefined') return 'development';
  
  const hostname = window.location.hostname;
  
  // Development: localhost
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'development';
  }
  
  // Staging: app.beta.tempstars.com
  if (hostname.includes('beta') || hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  }
  
  // Production: app.tempstars.com
  return 'production';
};

const IS_PRODUCTION = getEnvironment() === 'production';
const IS_STAGING = getEnvironment() === 'staging';
const IS_DEVELOPMENT = getEnvironment() === 'development';

/**
 * Platform detection (web vs mobile app)
 * TempStars is a hybrid Cordova app that runs on web, iOS, and Android
 */
const getPlatform = (): 'web' | 'ios' | 'android' => {
  if (typeof window === 'undefined') return 'web';
  
  const cordova = (window as any).cordova;
  
  if (!cordova) return 'web';
  
  if (cordova.platformId === 'ios') return 'ios';
  if (cordova.platformId === 'android') return 'android';
  
  return 'web';
};

/**
 * Check if running in Cordova mobile app
 */
const isMobileApp = (): boolean => {
  return !!(window as any).cordova;
};

/**
 * SHA-256 hashing using Web Crypto API (built-in, no dependencies)
 */
const sha256 = async (message: string): Promise<string> => {
  try {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('[Facebook Pixel] SHA-256 hashing failed:', error);
    return '';
  }
};

/**
 * Normalize and hash email for Advanced Matching
 */
const hashEmail = async (email: string): Promise<string> => {
  if (!email) return '';
  const normalized = email.toLowerCase().trim();
  return await sha256(normalized);
};

/**
 * Normalize and hash phone for Advanced Matching
 * Removes all non-numeric characters
 */
const hashPhone = async (phone: string | number): Promise<string> => {
  if (!phone) return '';
  const normalized = String(phone).replace(/\D/g, ''); // Remove non-digits
  return await sha256(normalized);
};

/**
 * Normalize and hash name for Advanced Matching
 */
const hashName = async (name: string): Promise<string> => {
  if (!name) return '';
  const normalized = name.toLowerCase().trim();
  return await sha256(normalized);
};

/**
 * Generate unique event ID for deduplication
 * Uses timestamp + random to ensure uniqueness
 */
const generateEventId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${random}`;
};

/**
 * Get attribution parameters from URL
 * Captures campaign attribution data and click IDs
 */
const CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'dclid',
  'ko_click_id',
  'li_fat_id',
  'msclkid',
  'ttclid',
  'twclid',
  'gbraid',
  'wbraid',
];

const getUTMParameters = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const clickIdParams = CLICK_ID_PARAMS.reduce<Record<string, string | null>>((acc, key) => {
    acc[key] = params.get(key);
    return acc;
  }, {});

  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
    ...clickIdParams,
  };
};

/**
 * Get stored attribution parameters from session storage
 * These are captured on first landing and persisted through the session
 */
const getStoredUTMParameters = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem('tempstars_utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Facebook Pixel] Failed to read stored UTM params:', error);
    return {};
  }
};

/**
 * Store attribution parameters in session storage
 * Called on landing page to persist attribution through sign-up flow
 */
export const captureUTMParameters = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const params = getUTMParameters();
    // Only store if we have at least one attribution parameter
    if (Object.values(params).some(v => v !== null)) {
      sessionStorage.setItem('tempstars_utm_params', JSON.stringify(params));
      console.log('[Facebook Pixel] Attribution parameters captured:', params);
    }
  } catch (error) {
    console.error('[Facebook Pixel] Failed to store UTM params:', error);
  }
};

/**
 * Interface for user data used in Advanced Matching
 */
interface UserData {
  email?: string;
  phone?: string | number;
  firstName?: string;
  lastName?: string;
  country?: number; // 0 = Canada, 1 = US
  province?: string;
  city?: string;
}

/**
 * Interface for Complete Registration event parameters
 */
interface CompleteRegistrationParams {
  userType: UserType;
  userData?: UserData;
  eventId?: string;
}

/**
 * Tracks the start of the registration process
 * Fires when user is about to submit email/password on Landing page
 * This is the TRUE top of the conversion funnel
 */
export const trackSignUpStart = (): void => {
  const platform = getPlatform();
  
  // Don't fire in development
  if (IS_DEVELOPMENT) {
    console.log(`[Facebook Pixel] DEV MODE: Would track InitiateCheckout (Platform: ${platform})`);
    return;
  }
  
  // Warn if on iOS (pixel may not work reliably)
  if (platform === 'ios') {
    console.warn('[Facebook Pixel] iOS WebView detected - pixel tracking may be unreliable due to ITP restrictions');
  }
  
  if (typeof window.fbq === 'function') {
    try {
      const eventId = generateEventId();
      const utmParams = getStoredUTMParameters();
      
      // Filter out null values
      const cleanUTMParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v !== null)
      );
      
      window.fbq('track', 'InitiateCheckout', {
        content_name: 'Sign Up Start',
        content_category: 'Registration',
        ...cleanUTMParams,
      }, {
        eventID: eventId,
      });
      
      // Store event ID for potential server-side deduplication
      sessionStorage.setItem('fb_pixel_signup_start_event_id', eventId);
      
      const env = IS_STAGING ? '[STAGING]' : '';
      console.log(`[Facebook Pixel] ${env} InitiateCheckout tracked`, {
        platform, // Track which platform for monitoring
        eventId,
        utmParams: cleanUTMParams
      });
    } catch (error) {
      console.error(`[Facebook Pixel] Error tracking InitiateCheckout (Platform: ${platform}):`, error);
    }
  } else {
    console.warn(`[Facebook Pixel] fbq not available on ${platform} - InitiateCheckout not tracked`);
    if (platform === 'ios') {
      console.warn('[Facebook Pixel] iOS WebView may block Facebook Pixel. Consider implementing Facebook SDK for Apps for reliable mobile tracking.');
    }
  }
};

/**
 * Tracks the completion of registration for a specific user type
 * Fires when user successfully completes their profile
 * 
 * Uses Advanced Matching to pass hashed user data for better attribution
 * Includes UTM parameters for campaign tracking
 * Uses eventID for deduplication with Conversions API
 * 
 * @param params - Configuration object with user type, user data, and optional event ID
 * @returns The eventID used for tracking (for potential CAPI integration)
 */
export const trackSignUpComplete = async (params: CompleteRegistrationParams): Promise<string | undefined> => {
  const { userType, userData, eventId: providedEventId } = params;
  const platform = getPlatform();
  
  // Don't fire in development
  if (IS_DEVELOPMENT) {
    console.log(`[Facebook Pixel] DEV MODE: Would track CompleteRegistration (Platform: ${platform})`, {
      userType,
      userData: userData ? 'present' : 'missing'
    });
    return undefined;
  }
  
  // Warn if on iOS (pixel may not work reliably in iOS WebView)
  if (platform === 'ios') {
    console.warn('[Facebook Pixel] iOS WebView detected - Manual Advanced Matching may have reduced effectiveness due to iOS privacy restrictions');
  }
  
  if (typeof window.fbq === 'function') {
    try {
      // Generate or use provided event ID
      const eventId = providedEventId || generateEventId();
      
      // Get UTM parameters
      const utmParams = getStoredUTMParameters();
      const cleanUTMParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v !== null)
      );
      
      // Prepare base event data
      const eventData: Record<string, any> = {
        content_name: userType,
        status: 'completed',
        value: 0, // No monetary value for free sign-ups
        currency: 'USD',
        ...cleanUTMParams,
      };
      
      // Add geographic data if available
      if (userData?.country) {
        eventData.country = userData.country === 0 ? 'CA' : 'US';
      }
      if (userData?.province) {
        eventData.region = userData.province;
      }
      if (userData?.city) {
        eventData.city = userData.city;
      }
      
      // Build Advanced Matching data (hashed)
      const advancedMatchingData: Record<string, string> = {};
      
      if (userData?.email) {
        advancedMatchingData.em = await hashEmail(userData.email);
      }
      if (userData?.phone) {
        advancedMatchingData.ph = await hashPhone(userData.phone);
      }
      if (userData?.firstName) {
        advancedMatchingData.fn = await hashName(userData.firstName);
      }
      if (userData?.lastName) {
        advancedMatchingData.ln = await hashName(userData.lastName);
      }
      
      // Merge Advanced Matching data with event data
      const finalEventData = {
        ...eventData,
        ...advancedMatchingData,
      };
      
      // Fire the pixel event
      window.fbq('track', 'CompleteRegistration', finalEventData, {
        eventID: eventId,
      });
      
      // Store event ID for potential server-side verification
      sessionStorage.setItem('fb_pixel_signup_complete_event_id', eventId);
      
      const env = IS_STAGING ? '[STAGING]' : '';
      console.log(`[Facebook Pixel] ${env} CompleteRegistration tracked`, {
        platform, // CRITICAL: Track which platform for mobile vs web analysis
        userType,
        eventId,
        hasEmail: !!userData?.email,
        hasPhone: !!userData?.phone,
        hasName: !!(userData?.firstName && userData?.lastName),
        country: eventData.country,
        region: eventData.region,
        utmParams: cleanUTMParams,
        advancedMatchingDataKeys: Object.keys(advancedMatchingData),
      });
      
      // Return eventId so calling code can send to server for CAPI
      return eventId;
    } catch (error) {
      console.error(`[Facebook Pixel] Error tracking CompleteRegistration (Platform: ${platform}):`, error);
      return undefined;
    }
  } else {
    console.warn(`[Facebook Pixel] fbq not available on ${platform} - CompleteRegistration not tracked`);
    if (platform === 'ios') {
      console.warn('[Facebook Pixel] RECOMMENDATION: Implement Facebook SDK for Apps for reliable iOS mobile tracking. Facebook Pixel is unreliable in iOS WebView due to Intelligent Tracking Prevention (ITP).');
    }
    return undefined;
  }
};

/**
 * Track a custom event for additional insights
 * Useful for tracking intermediate steps in the funnel
 * 
 * @param eventName - Custom event name
 * @param parameters - Optional event parameters
 */
export const trackCustomEvent = (
  eventName: string,
  parameters?: Record<string, any>
): void => {
  // Don't fire in development
  if (IS_DEVELOPMENT) {
    console.log(`[Facebook Pixel] DEV MODE: Would track custom event '${eventName}'`, parameters);
    return;
  }
  
  if (typeof window.fbq === 'function') {
    try {
      const eventId = generateEventId();
      const utmParams = getStoredUTMParameters();
      const cleanUTMParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v !== null)
      );
      
      window.fbq('trackCustom', eventName, {
        ...parameters,
        ...cleanUTMParams,
      }, {
        eventID: eventId,
      });
      
      const env = IS_STAGING ? '[STAGING]' : '';
      console.log(`[Facebook Pixel] ${env} Custom event '${eventName}' tracked`, {
        eventId,
        parameters,
        utmParams: cleanUTMParams
      });
    } catch (error) {
      console.error('[Facebook Pixel] Error tracking custom event:', error);
    }
  } else {
    console.warn('[Facebook Pixel] fbq not available - custom event not tracked');
  }
};

/**
 * Track ViewContent event (user views a specific page)
 * Useful for remarketing and funnel analysis
 * 
 * @param contentName - Name of the content/page being viewed
 * @param contentCategory - Category of content
 */
export const trackViewContent = (contentName: string, contentCategory?: string): void => {
  // Don't fire in development
  if (IS_DEVELOPMENT) {
    console.log('[Facebook Pixel] DEV MODE: Would track ViewContent', { contentName, contentCategory });
    return;
  }
  
  if (typeof window.fbq === 'function') {
    try {
      const eventId = generateEventId();
      const utmParams = getStoredUTMParameters();
      const cleanUTMParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v !== null)
      );
      
      window.fbq('track', 'ViewContent', {
        content_name: contentName,
        content_category: contentCategory || 'Registration',
        ...cleanUTMParams,
      }, {
        eventID: eventId,
      });
      
      const env = IS_STAGING ? '[STAGING]' : '';
      console.log(`[Facebook Pixel] ${env} ViewContent tracked`, {
        contentName,
        contentCategory,
        eventId
      });
    } catch (error) {
      console.error('[Facebook Pixel] Error tracking ViewContent:', error);
    }
  }
};

/**
 * Track Lead event (user shows interest, e.g., selects role)
 * Used between InitiateCheckout and CompleteRegistration
 * 
 * @param leadType - Type of lead (e.g., 'Role Selected')
 */
export const trackLead = (leadType: string, additionalData?: Record<string, any>): void => {
  // Don't fire in development
  if (IS_DEVELOPMENT) {
    console.log('[Facebook Pixel] DEV MODE: Would track Lead', { leadType, additionalData });
    return;
  }
  
  if (typeof window.fbq === 'function') {
    try {
      const eventId = generateEventId();
      const utmParams = getStoredUTMParameters();
      const cleanUTMParams = Object.fromEntries(
        Object.entries(utmParams).filter(([_, v]) => v !== null)
      );
      
      window.fbq('track', 'Lead', {
        content_name: leadType,
        ...additionalData,
        ...cleanUTMParams,
      }, {
        eventID: eventId,
      });
      
      const env = IS_STAGING ? '[STAGING]' : '';
      console.log(`[Facebook Pixel] ${env} Lead tracked`, {
        leadType,
        additionalData,
        eventId
      });
    } catch (error) {
      console.error('[Facebook Pixel] Error tracking Lead:', error);
    }
  }
};

/**
 * Get current environment for debugging
 */
export const getPixelEnvironment = (): string => {
  return getEnvironment();
};

/**
 * Get current platform (web, iOS, or Android)
 * Useful for monitoring mobile vs web conversion split
 */
export const getPixelPlatform = (): string => {
  return getPlatform();
};

/**
 * Check if running in mobile app (iOS or Android)
 */
export const isPixelInMobileApp = (): boolean => {
  return isMobileApp();
};

/**
 * Check if pixel should fire in current environment
 */
export const shouldFirePixel = (): boolean => {
  return IS_PRODUCTION || IS_STAGING;
};
